import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Avatar } from "@/components/common";
import { SharedFact } from "@/components/Repost";
import { FactActions } from "@/components/FactActions";
import { CommentRow } from "@/components/Comments";
import { BumpLine, REACTION } from "@/components/Reactions";
import { useMe } from "@/context/MeContext";

export const useFactActivity = (enabled, tick = 0) => {
  const [items, setItems] = useState([]);
  const [v, setV] = useState(0);
  // Your own reaction, comment or repost moves that Fact to the top right away.
  useEffect(() => { const bump = () => setV((x) => x + 1); window.addEventListener("middle:fact-activity", bump); return () => window.removeEventListener("middle:fact-activity", bump); }, []);
  useEffect(() => {
    if (!enabled) return setItems([]);
    api.get("/feed/fact-activity").then((r) => setItems(r.data)).catch(() => {});
  }, [enabled, tick, v]);
  return items;
};

const VERB = { commented: "commented", reposted: "reposted" };
const verbs = (actors) => {
  const all = new Set(actors.flatMap((a) => a.actions));
  const reacted = [...all].filter((a) => a.startsWith("reacted:")).map((a) => REACTION[a.slice(8)]?.emoji).filter(Boolean);
  return [reacted.length && `reacted ${[...new Set(reacted)].join("")}`, ...["commented", "reposted"].filter((v) => all.has(v)).map((v) => VERB[v])].filter(Boolean).join(" · ");
};

// One card per Fact that people you follow reacted to, commented on or reposted, with its reactions and latest comments.
export const FactActivityCard = ({ f }) => {
  const { me } = useMe();
  const [first, ...rest] = f.actors;
  const [all, setAll] = useState(false);
  const isMe = first.user.user_id === me?.user_id;
  return (
    <article className="surface p-4 sm:p-5 fade-up" data-testid={`fact-activity-card-${f.id}`}>
      <div className="flex items-center gap-2 text-xs text-slate-500" data-testid={`fact-activity-who-${f.id}`}>
        <span className="flex -space-x-2">{f.actors.slice(0, 3).map((a) => <Avatar key={a.user.user_id} user={a.user} size={24} />)}</span>
        <span className="min-w-0"><Link to={`/u/${first.user.user_id}`} className="font-semibold text-slate-800 hover:underline">{isMe ? "You" : first.user.name}</Link>
          {rest.length > 0 && <> and {rest.length} other{rest.length === 1 ? "" : "s"}{rest.some((a) => a.user.user_id === me?.user_id) ? " including you" : " you follow"}</>} <span>{verbs(f.actors)}</span></span>
      </div>
      <BumpLine by={f.bumped_by} meId={me?.user_id} testid={`fact-activity-bumped-by-${f.id}`} className="mt-2" />
      <SharedFact f={f} testid={`fact-activity-fact-${f.id}`} />
      {!all && f.latest_comments?.length > 0 && (
        <div className="mt-3 space-y-2.5" data-testid={`fact-activity-comments-${f.id}`}>
          {f.comment_count > f.latest_comments.length && <Link to={`/facts/${f.id}#comments`} className="text-xs font-medium text-slate-500 hover:underline" data-testid={`fact-activity-all-comments-${f.id}`}>View all {f.comment_count} comments</Link>}
          {f.latest_comments.map((c) => <CommentRow key={c.id} comment={c} />)}
        </div>)}
      <FactActions f={f} inline onOpenChange={setAll} className="mt-3 border-t border-border pt-2" />
    </article>
  );
};
